import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { useProps } from "contexts/PropsContext"

import { Translate } from "../../helpers/token.helpers"

/**
 * The two ways a $TOKEN tab can have nothing to show, told apart on purpose.
 *
 * They look identical to a naive error handler and mean opposite things:
 *
 *   "disabled"    — the gate is CLOSED for this tenant (`tokenEnabled &&
 *                   isMicroserviceOwner`). A configuration state. Waiting will
 *                   not fix it, and calling it an outage sends an operator
 *                   chasing an incident that does not exist.
 *   "unavailable" — token-service is unreachable (HTTP 503). A genuine outage,
 *                   and retrying is the right advice.
 *
 * There is deliberately no local fallback behind either: the monolith holds NO
 * $TOKEN state, so "degrading" to a cached figure would mean inventing one.
 */
export const TokenUnavailable = ({ variant, onRetry }: { variant: "disabled" | "unavailable"; onRetry?: () => void }) => {
  const { t } = useTranslation("translation", { useSuspense: false }) as { t: Translate }
  const { darkMode } = useProps()

  const disabled = variant === "disabled"

  return (
    <div className={clsx("rounded-lg border p-8 text-center", darkMode ? "border-[rgba(255,255,255,0.1)] bg-[#0C0C1A]" : "border-gray-200 bg-white")}>
      <p className={clsx("text-lg font-medium", darkMode ? "text-white" : "text-gray-900")}>
        {disabled ? t("token.gateDisabledTitle") : t("token.gateUnavailableTitle")}
      </p>

      <p className={clsx("mx-auto mt-3 max-w-xl text-sm", darkMode ? "text-gray-400" : "text-gray-600")}>
        {/* The flag name is interpolated rather than written into the copy so a
            rename lands in one place and never has to be re-translated. */}
        {disabled ? t("token.gateDisabledBody", { flag: "TOKEN_SERVICE_ENABLED" }) : t("token.gateUnavailableBody")}
      </p>

      {!disabled && onRetry && (
        <button type="button" onClick={onRetry} className="mt-6 rounded-lg bg-[rgba(132,0,168,1)] px-6 py-2 text-white transition-all hover:bg-[rgba(169,78,206,1)]">
          {t("token.retry")}
        </button>
      )}
    </div>
  )
}
